// @flow
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import {useForm, Controller} from 'react-hook-form';
import {getFilledValues} from '../../../../utils/form';
import {useCommonStyles} from '../../common/components/CommonStyle';
import Collapsible from '../../../../elements/Collapsible/Collapsible';

import type {
  Store,
  Contact,
  ContactType,
  StoreType,
  Country,
} from '../../../../entities';

import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import TelInput from '../../../../elements/TelInput/TelInput';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import {useMemo} from 'react';
import {
  getCurrencyByCountry,
  getCurrencyInSpanish,
} from '../../../on-boarding/utils';
import CustomExitPrompt from '../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {useIsFeatureInTier} from '../../../../utils/component';

import type {
  SaveGeneralSettingsRequest,
  SaveGeneralContactRequest,
} from '../api';
import {REGEX} from '../../../../constants';
import {InputAdornment, IconButton, Typography} from '@material-ui/core';
import {Delete, Edit} from '@material-ui/icons';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import {useStoreContacts} from '../utils';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {sanitizePhoneNumber} from '../../../../utils/phone';

type Props = {
  handleSaveGeneralSettings: (data: SaveGeneralSettingsRequest) => void,
  store: Store,
  currency: Object,
  setCurrency: ({currencyId: string}) => void,
  countries?: Country[],
  country: Country,
  fetchCountryError?: string,
  saveStoreCountry: ({countryId: string}) => void,
  storeTypes?: StoreType[],
  isSaving?: boolean,
  contact?: Contact[],
  contactTypes?: ContactType[],
  handleSaveGeneralContact: (dataContact: SaveGeneralContactRequest[]) => void,
};

const contactTypesInfo = {
  PHONE_NUMBER: {
    label: 'Teléfono',
    form: {
      pattern: {
        value: /^[0-9]{9,11}$/i,
        message: 'Formato de teléfono inválido',
      },
    },
  },
  CORPORATE_EMAIL: {
    label: 'E-mail',
    form: {
      pattern: {
        value: REGEX.email,
        message: 'Formato de email inválido',
      },
    },
  },
  WHATS_APP_NUMBER: {
    label: 'WhatsApp',
    form: {
      required: 'Campo requerido',
      pattern: {
        value: /^[0-9]{9,11}$/i,
        message: 'Formato de teléfono inválido',
      },
    },
  },
  ADDRESS: {
    label: 'Dirección',
    form: {},
  },
};

// this is necessary because the address and map url is saved in the same field in database
const ADDRESS_STRING_SEPARATOR = '@@@@@@';

export default function GeneralSettings(props: Props) {
  const {
    store,
    currency,
    setCurrency,
    countries,
    country,
    fetchCountryError,
    saveStoreCountry,
    storeTypes,
    isSaving,
    handleSaveGeneralSettings,
    contact,
    contactTypes,
    handleSaveGeneralContact,
  } = props;

  const commonClasses = useCommonStyles();

  const [addContactModalOpen, setAddContactModalOpen] = useState(false);

  const [newAddress, setNewAddress] = useState('');
  const [newAddressUrl, setNewAddressUrl] = useState('');
  const [addressContactIdToEdit, setAddressContactIdToEdit] = useState();

  const hasStoreInformationFeature = useIsFeatureInTier('STORE_INFORMATION');

  const [contactStoreIdToDelete, setContactStoreIdToDelete] = useState();

  const {handleSubmit, register, errors, control, formState, reset} = useForm();

  const {isDirty} = formState;
  const {deleteStoreContact} = useStoreContacts();

  const getDefaultValue = (storeContactId: string) => {
    const c = contact && contact.find(t => t.id === storeContactId);
    if (c) return c.data;
  };

  const handleCloseModal = () => {
    setAddContactModalOpen(false);
    setNewAddress('');
    setNewAddressUrl('');
    setAddressContactIdToEdit(null);
  };

  const handleAddContact = () => {
    if (newAddress) {
      const addressType = contactTypes?.find(ct => ct.name === 'ADDRESS');
      if (!addressType) return;
      const addressWithUrl = `${newAddress}${ADDRESS_STRING_SEPARATOR}${newAddressUrl}`;

      if (addressContactIdToEdit) {
        handleSaveGeneralContact([
          {
            data: addressWithUrl,
            contactTypeId: addressType.id,
            storeContactId: addressContactIdToEdit,
          },
        ]);
      } else {
        handleSaveGeneralContact([
          {
            data: addressWithUrl,
            contactTypeId: addressType.id,
          },
        ]);
      }

      handleCloseModal();
    }
  };

  // const defaultCountry = useMemo(() => {
  //   if (countries && currency.country) {
  //     return countries.find(c => currency.country.code === c.code);
  //   }
  // }, [countries, currency.country]);

  const defaultCurrency = useMemo(() => {
    if (currency.currencies && currency.country) {
      return currency.currencies.find(
        c => getCurrencyByCountry(currency.country.code) === c.code
      );
    }
  }, [currency.country, currency.currencies]);

  const onSubmit = value => {
    if (store) {
      const data: SaveGeneralSettingsRequest = getFilledValues(
        {
          name: store.name === value.name ? undefined : value.name,
          storeTypeId:
            store.storeType &&
            store.storeType.id &&
            store.storeType.id === value.storeType
              ? undefined
              : value.storeType,
          // country
          /*whatsappNumber:
            store.contactInformation.whatsappNumber === value.whatsappNumber
              ? null
              : value.whatsappNumber,*/
        },
        ['name', 'storeTypeId']
      );
      if (Object.keys(data).length) {
        handleSaveGeneralSettings(data);
      }
      if (
        value.currency &&
        !(
          currency &&
          currency.currency &&
          currency.currency.id &&
          currency.currency.id === value.currency
        )
      )
        setCurrency({currencyId: value.currency});
      if (
        value.country &&
        (!country || (country && country.id && country.id !== value.country))
      )
        saveStoreCountry({
          countryId: value.country,
        });
    } else throw new Error('store is not defined');
    if (contactTypes && contact) {
      const r: SaveGeneralContactRequest[] = [];

      Object.keys(value).forEach((ctn: string) => {
        const ct = contact.find(t => t.id === ctn);

        if (!ct) return;
        const isPhone =
          ct.contactType.name === 'PHONE_NUMBER' ||
          ct.contactType.name === 'WHATS_APP_NUMBER';
        // const c = contact.find(t => t.contactType.name === ctn);

        const data = value[ctn];
        const sc: SaveGeneralContactRequest = {
          data: isPhone ? sanitizePhoneNumber(data) : data,
          contactTypeId: ct.contactType.id,
          storeContactId: ct.id,
        };

        const isDirty = ct.data !== value[ctn];

        if (isDirty) r.push(sc);
      });

      if (r.length) handleSaveGeneralContact(r);
    }
    reset(value);
  };

  const addressContactList = contact?.filter(
    item => item.contactType.name === 'ADDRESS'
  );

  return (
    <Collapsible title="Información">
      {hasStoreInformationFeature && <CustomExitPrompt when={isDirty} />}

      <Grid container spacing={3}>
        <Grid item spacing={3} xs={12}>
          <Typography className={commonClasses.subtitle}>
            Direcciones de la tienda
          </Typography>
        </Grid>
        {addressContactList &&
          addressContactList.map(contactItem => {
            const ct = contactItem.contactType;
            const address = contactItem.data.split(ADDRESS_STRING_SEPARATOR)[0];
            const url = contactItem.data.split(ADDRESS_STRING_SEPARATOR)[1];
            const formattedValue = `${address}${url ? ` - ${url}` : ''}`;
            return (
              <Grid key={contactItem.id} item xs={12}>
                <CustomTextField
                  label={contactTypesInfo[ct.name].label}
                  fullWidth
                  name={contactItem.id}
                  autoComplete={ct.name}
                  disabled={isSaving}
                  value={formattedValue}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Eliminar campo"
                          color="primary"
                          onClick={() => {
                            setAddressContactIdToEdit(contactItem.id);
                            setNewAddress(address);
                            setNewAddressUrl(url);
                            setAddContactModalOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          aria-label="Eliminar campo"
                          className={commonClasses.deleteButton}
                          onClick={() =>
                            setContactStoreIdToDelete(contactItem.id)
                          }
                        >
                          <Delete />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            );
          })}
      </Grid>

      <CustomButton
        type="submit"
        color="primary"
        size="large"
        className={commonClasses.saveButton}
        disabled={isSaving}
        onClick={() => setAddContactModalOpen(true)}
      >
        Agregar Dirección +
      </CustomButton>

      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        style={{marginTop: '1rem'}}
      >
        <Grid container spacing={3}>
          <Grid item spacing={3} xs={12}>
            <Typography className={commonClasses.subtitle}>
              Información de la tienda
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextField
              label="Nombre de tienda"
              fullWidth
              name="name"
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              disabled={isSaving}
              defaultValue={store.name}
            />
          </Grid>
          {!!contactTypes &&
            !!contact &&
            contact
              .filter(contactItem => contactItem.contactType.name !== 'ADDRESS')
              .map(contactItem => {
                const ct = contactItem.contactType;
                if (
                  ct.name === 'PHONE_NUMBER' ||
                  ct.name === 'WHATS_APP_NUMBER'
                ) {
                  return (
                    <Grid key={ct.id} item xs={12} sm={4}>
                      <Controller
                        control={control}
                        name={contactItem.id}
                        defaultValue={getDefaultValue(contactItem.id)}
                        render={({onChange, value}) => (
                          <TelInput
                            label={contactTypesInfo[ct.name].label}
                            fullWidth
                            defaultValue={getDefaultValue(contactItem.id)}
                            name={contactItem.id}
                            disabled={isSaving}
                            onChange={v => {
                              onChange(v);
                            }}
                          />
                        )}
                      />
                    </Grid>
                  );
                }

                return (
                  <Grid key={contactItem.id} item xs={12} sm={4}>
                    <CustomTextField
                      label={contactTypesInfo[ct.name].label}
                      fullWidth
                      name={contactItem.id}
                      autoComplete={ct.name}
                      error={!!errors[ct.name]}
                      helperText={
                        errors[ct.name] ? errors[ct.name].message : ''
                      }
                      inputRef={register({
                        ...contactTypesInfo[ct.name].form,
                      })}
                      disabled={isSaving}
                      defaultValue={getDefaultValue(contactItem.id)}
                    />
                  </Grid>
                );
              })}
          {storeTypes && (
            <Grid item xs={12} sm={4}>
              <CustomSelect
                native
                fullWidth
                label="Rubro de la tienda"
                defaultValue={store.storeType && store.storeType.id}
                name="storeType"
                error={!!errors.storeType}
                helperText={errors.storeType ? errors.storeType.message : ''}
                inputRef={register({
                  // required: 'Campo obligatorio',
                })}
                disabled={isSaving}
              >
                <option value="" hidden />
                {storeTypes &&
                  storeTypes.map(storeType => (
                    <option
                      key={`storeType-option-${storeType.label}`}
                      value={storeType.id}
                    >
                      {storeType.label}
                    </option>
                  ))}
              </CustomSelect>
            </Grid>
          )}
          {(country || fetchCountryError) && (
            <Grid item xs={12} sm={4}>
              <CustomSelect
                native
                fullWidth
                label="País de origen"
                defaultValue={country ? country.id : ''}
                name="country"
                error={!!errors.country}
                helperText={errors.country ? errors.country.message : ''}
                inputRef={register()}
                // disabled={!!country || isSaving}
                disabled={isSaving}
              >
                <option value="" hidden />
                {countries &&
                  countries.map(country => (
                    <option
                      key={`country-option-${country.code}`}
                      value={country.id}
                    >
                      {country.name}
                    </option>
                  ))}
              </CustomSelect>
            </Grid>
          )}
          {currency.currencies && (
            <Grid item xs={12} sm={4}>
              <CustomSelect
                native
                fullWidth
                label="Moneda"
                defaultValue={
                  currency.currency
                    ? currency.currency.id
                    : defaultCurrency
                    ? defaultCurrency.id
                    : ''
                }
                name="currency"
                error={!!errors.currency}
                helperText={errors.currency ? errors.currency.message : ''}
                inputRef={register({
                  // required: 'Campo obligatorio',
                })}
                disabled={isSaving}
              >
                <option value="" hidden />
                {currency.currencies &&
                  currency.currencies.map(c => (
                    <option key={`currency-option-${c.code}`} value={c.id}>
                      {`${c.code} - ${getCurrencyInSpanish(c.code)}`}
                    </option>
                  ))}
              </CustomSelect>
            </Grid>
          )}
        </Grid>
        {hasStoreInformationFeature && (
          <CustomButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            className={commonClasses.saveButton}
            disabled={isSaving}
          >
            Guardar cambios
          </CustomButton>
        )}
      </form>

      {!!contactStoreIdToDelete && (
        <ConfirmModal
          open
          message="¿Estás seguro que deseas eliminar este contacto?"
          onCancel={() => setContactStoreIdToDelete(null)}
          onClose={() => setContactStoreIdToDelete(null)}
          onConfirm={() => {
            if (!contactStoreIdToDelete) return;
            deleteStoreContact(contactStoreIdToDelete).then(() => {
              setContactStoreIdToDelete(null);
            });
          }}
        />
      )}

      <CustomModal
        open={addContactModalOpen}
        handleCloseModal={handleCloseModal}
      >
        <div className={commonClasses.modal}>
          <Typography className={commonClasses.title}>
            Agregar dirección
          </Typography>
          <div className={commonClasses.form}>
            <CustomTextField
              label={contactTypesInfo.ADDRESS.label}
              fullWidth
              name={contactTypesInfo.ADDRESS.label}
              onChange={e => setNewAddress(e.target.value)}
              value={newAddress}
            />
            <CustomTextField
              label="Url de Google Maps"
              fullWidth
              name="url"
              value={newAddressUrl}
              onChange={e => setNewAddressUrl(e.target.value)}
            />
          </div>
          <div className={commonClasses.options}>
            <CustomButton
              onClick={handleCloseModal}
              className={commonClasses.button}
              color="default"
              fullWidth
            >
              Cancelar
            </CustomButton>
            <CustomButton
              className={commonClasses.button}
              variant="contained"
              onClick={handleAddContact}
              disabled={!newAddress}
              fullWidth
            >
              Agregar
            </CustomButton>
          </div>
        </div>
      </CustomModal>
    </Collapsible>
  );
}

GeneralSettings.propTypes = {
  handleSaveGeneralSettings: PropTypes.func.isRequired,
  store: PropTypes.object.isRequired,
  currency: PropTypes.object,
  setCurrency: PropTypes.func.isRequired,
  countries: PropTypes.array,
  country: PropTypes.object,
  fetchCountryError: PropTypes.string,
  saveStoreCountry: PropTypes.func.isRequired,
  storeTypes: PropTypes.array,
  isSaving: PropTypes.bool,
};
